import {
  Calendar,
  Command,
  Shift,
  ShiftsGroupEvent,
  UserRole,
} from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useMemo } from 'react'
import { View } from 'react-native'
import { Card } from 'shared/components/card'
import {
  HelperScheduleShift,
  getHelperScheduleShiftHeight,
} from './shifts/helper_schedule'
import {
  OPEN_SHIFT_CARD_HEIGHT,
  OPEN_SHIFT_TOP_MARGIN,
} from './shifts/open_shift_schedule'
import {
  StoreScheduleShift,
  getStoreScheduleShiftHeight,
} from './shifts/store_schedule'
import { EVENT_TIME_HEIGHT, Time } from './time'
import { EventViewStyleProps, ShiftViewProps } from './types'

interface EventViewProps {
  role: UserRole
  calendar: Calendar
  event: ShiftsGroupEvent
  shift: Shift
  hasSummary: boolean
  onShiftDetails(shift: Shift): void
  onCommandButtonPress(command: Command): Promise<void>
}

export const SHIFT_TOP_MARGIN = 3

export function ShiftView({
  role,
  calendar,
  event,
  shift,
  hasSummary,
  onShiftDetails,
  onCommandButtonPress,
}: EventViewProps) {
  const ChildShiftView = useMemo(() => getShiftView(calendar), [calendar])

  const isPastMyShift = calendar === 'helper' && shift.isPastShift
  const showEventTime = !hasSummary && !isPastMyShift

  const height = useMemo(
    () => getShiftHeight(calendar, role, shift, showEventTime),
    [calendar, role, shift, showEventTime]
  )

  const styles = useStyles({ height, isPastMyShift })

  return (
    <Card
      style={styles.card}
      critical={shift.critical}
      alert={calendar === 'store' && !!shift.note}
    >
      {showEventTime ? (
        <Time event={event} showAddedByNext={!shift.imported} />
      ) : null}
      <ChildShiftView
        shift={shift}
        role={role}
        onShiftDetails={onShiftDetails}
        onCommandButtonPress={onCommandButtonPress}
      />
      {!showEventTime && !shift.imported ? (
        <View style={styles.importedStatusContainer}>
          <Text style={styles.importedStatusText}>{t('addedByNext')}</Text>
        </View>
      ) : null}
    </Card>
  )
}

function getShiftView(
  calendar: Calendar
): (props: ShiftViewProps) => React.JSX.Element {
  switch (calendar) {
    case 'helper':
      return HelperScheduleShift
    case 'store':
      return StoreScheduleShift
  }
}

export function getShiftHeight(
  calendar: Calendar,
  role: UserRole,
  shift: Shift,
  showEventTime: boolean
): number {
  const eventTimeHeight = showEventTime ? EVENT_TIME_HEIGHT : 0
  switch (calendar) {
    case 'helper':
      if (shift.open) {
        return OPEN_SHIFT_CARD_HEIGHT
      }
      return getHelperScheduleShiftHeight(role, shift) + eventTimeHeight
    case 'store':
      return getStoreScheduleShiftHeight(role, shift) + eventTimeHeight
  }
}

export function getShiftTopMargin(isOpenShiftsSchedule?: boolean) {
  return isOpenShiftsSchedule ? OPEN_SHIFT_TOP_MARGIN : SHIFT_TOP_MARGIN
}

const useStyles = makeStyles((theme, props: EventViewStyleProps) => ({
  card: {
    height: props.height,
    marginTop: SHIFT_TOP_MARGIN,
    backgroundColor: props.isPastMyShift
      ? theme.colors?.grey4
      : theme.mode === 'dark'
      ? theme.colors?.grey13
      : theme.colors?.white,
  },
  importedStatusContainer: {
    position: 'absolute',
    bottom: 5,
    right: 5,
  },
  importedStatusText: {
    fontSize: 12,
  },
}))
